import { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Copy, Check, ExternalLink, Wifi, Globe2, Clock,
  Shield, Zap, Code2, Users, Info, AlertCircle, Activity,
  CheckCircle2, XCircle, Loader2, ChevronRight, DollarSign,
  Download, Droplets, ThumbsUp, Wrench, MapPin, FileText,
  RefreshCw, FlaskConical, BarChart3, User, GitCompareArrows,
  Globe, Tag, MessageSquare, Key,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UptimeBadge, OnlineIndicator } from "@/components/relay/UptimeBadge";
import { SparklineChart } from "@/components/relay/SparklineChart";
import { UseCaseBadge } from "@/components/relay/UseCaseBadge";
import { NIP66Badge } from "@/components/relay/NIP66Badge";
import { VotingPanel } from "@/components/relay/VotingPanel";
import { UptimeHistoryChart } from "@/components/charts/UptimeHistoryChart";
import { AutoTagsPanel } from "@/components/relay/AutoTagsPanel";
import { AddToRelayListButton } from "@/components/relay/AddToRelayListButton";
import { RelayCommentSection } from "@/components/relay/RelayCommentSection";
import { RelayLabelPanel } from "@/components/relay/RelayLabelPanel";
import { RelayMembershipPanel } from "@/components/relay/RelayMembershipPanel";
import { RelayManagementPanel } from "@/components/relay/RelayManagementPanel";
import { MonitorConsensusCard } from "@/components/relay/MonitorConsensusCard";
import { RealUptimePanel } from "@/components/relay/RealUptimePanel";
import { PeerBenchmarksCard } from "@/components/relay/PeerBenchmarksCard";
import { Nip11ValidationCard } from "@/components/relay/Nip11ValidationCard";
import { FavoriteButton, TrustBadge, LabelBadges } from "@/components/relay/RelayCardExtras";
import { useRelayById } from "@/hooks/useRelayData";
import { useRelayTest } from "@/hooks/useRelayTest";
import { useLiveNIP11 } from "@/hooks/useLiveNIP11";
import { useNIP66Monitor, decodeGeohash } from "@/hooks/useNIP66Monitor";
import { useNIPVerifier, TESTABLE_NIPS, type NIPTestResult, type NIPVerifyStatus } from "@/hooks/useNIPVerifier";
import { useAuthor } from "@/hooks/useAuthor";
import { LiveStatusBadges, CheckNowButton } from "@/components/relay/LiveStatusBadges";
import { RelayBadges } from "@/components/relay/RelayBadges";
import { computeHealthScore, gradeColor, gradeBgColor, type HealthScoreBreakdown } from "@/lib/healthScore";
import type { LiveRelayRecord } from "@/hooks/useLiveRelayStore";
import { genUserName } from "@/lib/genUserName";
import { shortenUrl, getNipName, formatPrice, timeAgo, formatLatency, relayUrlToId } from "@/lib/utils";

const CLIENT_INSTRUCTIONS: Record<string, { name: string; steps: string[] }> = {
  damus: {
    name: "Damus (iOS)",
    steps: ["Open Damus", "Go to Settings → Relays", "Tap + and paste the relay URL", "Tap Add"],
  },
  primal: {
    name: "Primal",
    steps: ["Open Primal", "Go to Settings → Network → Relays", "Tap Add Relay", "Paste the URL and confirm"],
  },
  snort: {
    name: "Snort.social",
    steps: ["Open Snort.social", "Click on Settings → Relays", "Click Add Relay", "Enter the relay URL"],
  },
  amethyst: {
    name: "Amethyst (Android)",
    steps: ["Open Amethyst", "Go to Settings → Network → Relays", "Tap Add Relay", "Paste the URL and save"],
  },
};

// ─── Health Score Component ──────────────────────────────────────────────
function HealthScoreCard({ relay }: { relay: LiveRelayRecord }) {
  const breakdown = useMemo(() => computeHealthScore(relay), [relay]);

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" />
          Health Score
          <span className="text-xs text-muted-foreground font-normal ml-auto">Transparent formula</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Score + Grade */}
        <div className="flex items-center gap-6 mb-6">
          <div className={`w-16 h-16 rounded-2xl border-2 flex items-center justify-center ${gradeBgColor(breakdown.grade)}`}>
            <span className={`text-3xl font-black ${gradeColor(breakdown.grade)}`}>{breakdown.grade}</span>
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground">Health Score</div>
            <div className="text-3xl font-black">{breakdown.total}<span className="text-lg text-muted-foreground">/100</span></div>
          </div>
        </div>

        {/* Component breakdown */}
        <div className="space-y-3">
          {breakdown.components.map((comp) => (
            <div key={comp.name}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium">{comp.name}</span>
                <span className="text-muted-foreground">
                  {comp.points.toFixed(1)} / {comp.maxPoints}
                </span>
              </div>
              <Progress value={comp.percent * 100} className="h-1.5" />
              <p className="text-xs text-muted-foreground mt-0.5">{comp.description}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── NIP Verification Panel ─────────────────────────────────────────────
function NIPVerificationPanel({ relay }: { relay: LiveRelayRecord }) {
  const { report, running, verify, reset } = useNIPVerifier();
  const nips = relay.liveNip11?.supported_nips ?? relay.nip11?.supported_nips ?? [];

  const statusIcon = (status: NIPVerifyStatus) => {
    switch (status) {
      case 'verified': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'testing': return <Loader2 className="w-4 h-4 text-primary animate-spin" />;
      case 'unsupported': return <AlertCircle className="w-4 h-4 text-muted-foreground/40" />;
      default: return <div className="w-4 h-4 rounded-full border border-border" />;
    }
  };

  const statusLabel = (status: NIPVerifyStatus) => {
    switch (status) {
      case 'verified': return 'Verified';
      case 'failed': return 'Failed';
      case 'testing': return 'Testing...';
      case 'unsupported': return 'No test';
      default: return 'Pending';
    }
  };

  const statusColor = (status: NIPVerifyStatus) => {
    switch (status) {
      case 'verified': return 'text-emerald-500';
      case 'failed': return 'text-red-500';
      case 'testing': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const handleVerify = () => {
    reset();
    verify(relay.url, nips);
  };

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-primary" />
          NIP Verification
          {report && (
            <Badge variant="secondary" className="text-xs ml-auto">
              {report.verified} verified / {report.failed} failed
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4">
          Test whether this relay's claimed NIP support actually works. We open a live WebSocket
          and send targeted test messages for each testable NIP.
        </p>

        <div className="flex items-center gap-3 mb-4">
          <Button
            onClick={handleVerify}
            disabled={running || nips.length === 0}
            size="sm"
            className="gap-2"
          >
            {running ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Testing {nips.length} NIPs...</>
            ) : (
              <><FlaskConical className="w-4 h-4" /> Verify NIP Support</>
            )}
          </Button>

          {report && !running && (
            <div className="flex items-center gap-2 text-sm">
              {report.connectionOk ? (
                <><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Connected in {report.connectionLatencyMs}ms</>
              ) : (
                <><XCircle className="w-4 h-4 text-red-500" /> Connection failed</>
              )}
            </div>
          )}

          {report && (
            <div className="ml-auto text-xs text-muted-foreground">
              Score: <span className="font-bold">{report.verifyScore}%</span>
            </div>
          )}
        </div>

        {/* Results grid */}
        {report && report.results.length > 0 && (
          <div className="space-y-1.5">
            {report.results.map((r) => (
              <div
                key={r.nip}
                className="flex items-center gap-3 py-2 px-3 rounded-lg border border-border/30 bg-card/50 hover:bg-card/80 transition-colors"
              >
                {statusIcon(r.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-primary">
                      NIP-{String(r.nip).padStart(2, '0')}
                    </span>
                    <span className="text-xs text-muted-foreground">{r.name}</span>
                  </div>
                  {r.detail && (
                    <p className="text-xs text-muted-foreground/80 mt-0.5 truncate">{r.detail}</p>
                  )}
                </div>
                <span className={`text-xs font-medium ${statusColor(r.status)}`}>
                  {statusLabel(r.status)}
                </span>
                {r.latencyMs != null && r.status !== 'unsupported' && (
                  <span className="text-xs text-muted-foreground font-mono">{r.latencyMs}ms</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Legend when no report */}
        {!report && nips.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[
              { icon: <CheckCircle2 className="w-3 h-3 text-emerald-500" />, label: 'Verified', desc: 'Test passed' },
              { icon: <XCircle className="w-3 h-3 text-red-500" />, label: 'Failed', desc: 'Claimed but failed' },
              { icon: <AlertCircle className="w-3 h-3 text-muted-foreground/40" />, label: 'No test', desc: 'Cannot auto-test' },
              { icon: <div className="w-3 h-3 rounded-full border border-border" />, label: 'Pending', desc: 'Not yet tested' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5 text-muted-foreground">
                {l.icon} <span>{l.label}</span>
              </div>
            ))}
          </div>
        )}

        {nips.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No NIP support information available to verify.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Operator Profile Component ──────────────────────────────────────────
function OperatorProfile({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;
  const name = meta?.name ?? genUserName(pubkey);

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <User className="w-4 h-4 text-primary" />
          Relay Operator
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 mb-4">
          <Avatar className="w-12 h-12 border-2 border-primary/20">
            <AvatarImage src={meta?.picture} alt={name} />
            <AvatarFallback className="text-lg font-bold">{name.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm truncate">{name}</div>
            {meta?.nip05 && (
              <p className="text-xs text-primary truncate">{meta.nip05}</p>
            )}
            <p className="text-xs text-muted-foreground font-mono truncate">
              {pubkey.slice(0, 16)}...{pubkey.slice(-8)}
            </p>
          </div>
        </div>

        {meta?.about && (
          <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-3">{meta.about}</p>
        )}

        {meta?.website && (
          <a
            href={meta.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <Globe2 className="w-3 h-3" /> {meta.website}
          </a>
        )}

        {meta?.lud16 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
            <Zap className="w-3 h-3 text-yellow-500" /> {meta.lud16}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function RelayDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { relay, loading, notFound } = useRelayById(id ?? "");
  const { result: testResult, test: runTest, reset: resetTest } = useRelayTest();
  const { data: liveNip11 } = useLiveNIP11(relay?.url ?? '', !!relay);
  const { data: monitorMap } = useNIP66Monitor();
  const [copied, setCopied] = useState(false);
  const [selectedClient, setSelectedClient] = useState("damus");
  const { data: nip11Info } = useLiveNIP11(relay?.url ?? '', !!relay);

  // Get NIP-66 monitor data for this relay
  const monitorEvent = relay ? monitorMap?.get(relay.url) : undefined;
  const geoCoords = monitorEvent?.geohash ? decodeGeohash(monitorEvent.geohash) : null;
  const relaySelfPubkey = nip11Info?.self ?? relay?.nip11?.self;

  const handleCopy = () => {
    if (!relay) return;
    navigator.clipboard.writeText(relay.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (notFound || !relay) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-20 text-center">
        <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Relay Not Found</h2>
        <p className="text-muted-foreground mb-6">This relay isn't in our directory yet.</p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Go Back
          </Button>
          <Link to="/submit">
            <Button className="gap-2">Submit This Relay</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Use live NIP-11 when available, fallback to stored
  const effectiveNip11 = liveNip11 ?? relay.nip11;
  const nips = effectiveNip11.supported_nips ?? [];
  const minPriceTier = relay.priceTiers.find((t) => t.price > 0) ?? relay.priceTiers[0];

  const testStatusIcon = {
    idle: null,
    connecting: <Loader2 className="w-4 h-4 animate-spin text-primary" />,
    connected: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
    failed: <XCircle className="w-4 h-4 text-red-500" />,
    timeout: <XCircle className="w-4 h-4 text-yellow-500" />,
  }[testResult.status];

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/relays" className="hover:text-foreground transition-colors flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Relay Explorer
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-foreground font-medium truncate">{relay.name}</span>
      </div>

      {/* Header Card */}
      <Card className="border-border/60 mb-6 overflow-hidden">
        <div className={`h-1 w-full ${relay.isOnline ? "bg-gradient-to-r from-emerald-500 via-primary to-violet-500" : "bg-gradient-to-r from-red-500 to-orange-500"}`} />
        <CardContent className="pt-5 pb-5">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            {/* Left: Name + URL */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <OnlineIndicator isOnline={relay.isOnline} />
                {relay.featured && (
                  <Badge variant="outline" className="border-primary/30 text-primary text-xs">
                    ⭐ Featured
                  </Badge>
                )}
                <UptimeBadge uptime={relay.uptimePercent30d} showLabel />
                {relay.nip66 && <NIP66Badge data={relay.nip66} size="md" />}
                {relay.blossomSupported && (
                  <span className="inline-flex items-center gap-1 text-xs bg-sky-500/10 text-sky-500 border border-sky-500/20 px-2 py-0.5 rounded-full font-medium">
                    <Droplets className="w-3 h-3" /> Blossom
                  </span>
                )}
                <TrustBadge relayUrl={relay.url} size="md" />
                {relay.importSources?.some((s) => s.source === 'xport.top') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-xs bg-orange-500/10 text-orange-500 border border-orange-500/20 px-2 py-0.5 rounded-full font-medium cursor-help">
                          <Download className="w-3 h-3" /> xport.top
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Stats imported from relays.xport.top CSV export</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-black mb-1">{relay.name}</h1>

              <div className="flex items-center gap-2 mb-3">
                <code className="text-sm font-mono text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-lg border border-border/40 flex-1 md:flex-none">
                  {relay.url}
                </code>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2 h-8" onClick={handleCopy}>
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Copy relay WSS URL</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {relay.websiteUrl && (
                  <a href={relay.websiteUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="gap-2 h-8">
                      <ExternalLink className="w-3.5 h-3.5" /> Website
                    </Button>
                  </a>
                )}
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                {relay.description}
              </p>

              {/* Use cases */}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {relay.useCases.map((uc) => (
                  <Link key={uc} to={`/relays?useCase=${encodeURIComponent(uc)}`}>
                    <UseCaseBadge tag={uc} size="md" />
                  </Link>
                ))}
              </div>

              {/* NIP-32 Labels */}
              <div className="mt-2">
                <LabelBadges relayUrl={relay.url} max={5} />
              </div>

              {/* Achievement Badges */}
              <div className="mt-3">
                <RelayBadges relay={relay as LiveRelayRecord} size="sm" />
              </div>
            </div>

            {/* Right: Key stats */}
            <div className="flex md:flex-col gap-3 flex-wrap md:flex-nowrap flex-shrink-0">
              <div className="bg-muted/40 rounded-xl px-4 py-3 text-center min-w-[100px]">
                <div className="text-2xl font-black text-emerald-500">{relay.uptimePercent30d.toFixed(1)}%</div>
                <div className="text-xs text-muted-foreground">30d Uptime</div>
              </div>
              <div className="bg-muted/40 rounded-xl px-4 py-3 text-center min-w-[100px]">
                <div className="text-2xl font-black text-primary">{relay.trustScore}</div>
                <div className="text-xs text-muted-foreground">Trust Score</div>
              </div>
              {relay.avgLatencyMs != null && (
                <div className="bg-muted/40 rounded-xl px-4 py-3 text-center min-w-[100px]">
                  <div className="text-2xl font-black">{relay.avgLatencyMs}ms</div>
                  <div className="text-xs text-muted-foreground">Avg Latency</div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add to relay list CTA */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <AddToRelayListButton relayUrl={relay.url} />
        <FavoriteButton relayUrl={relay.url} size="md" />
        <Link to={`/compare?r1=${encodeURIComponent(relay.url)}`}>
          <Button variant="outline" size="sm" className="gap-2 text-xs">
            <GitCompareArrows className="w-3.5 h-3.5" />
            Compare
          </Button>
        </Link>
        <Link to={`/atlas?relay=${encodeURIComponent(relay.url)}`}>
          <Button variant="outline" size="sm" className="gap-2 text-xs">
            <Globe className="w-3.5 h-3.5" />
            Show on Atlas
          </Button>
        </Link>
      </div>

      {/* Main content tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-muted/50 flex-wrap h-auto gap-0.5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="verify" className="flex items-center gap-1">
            <FlaskConical className="w-3 h-3" />
            Verify
          </TabsTrigger>
          <TabsTrigger value="uptime">Uptime</TabsTrigger>
          <TabsTrigger value="nip66" className="flex items-center gap-1">
            <Activity className="w-3 h-3" />
            NIP-66
          </TabsTrigger>
          <TabsTrigger value="community" className="flex items-center gap-1">
            <ThumbsUp className="w-3 h-3" />
            Community
          </TabsTrigger>
          <TabsTrigger value="comments" className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            Comments
          </TabsTrigger>
          <TabsTrigger value="labels" className="flex items-center gap-1">
            <Tag className="w-3 h-3" />
            Labels
          </TabsTrigger>
          <TabsTrigger value="membership" className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            Membership
          </TabsTrigger>
          <TabsTrigger value="autotags" className="flex items-center gap-1">
            <Code2 className="w-3 h-3" />
            Auto-Tags
          </TabsTrigger>
          <TabsTrigger value="nips">NIPs</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="add-to-client">Add to Client</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* NIP-11 Info */}
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="w-4 h-4 text-primary" />
                  Relay Information (NIP-11)
                  {liveNip11 && (
                    <span className="text-xs bg-emerald-500/15 text-emerald-500 border border-emerald-500/25 px-1.5 py-0.5 rounded-full font-medium ml-auto">
                      Live
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: "Name", value: effectiveNip11.name },
                  { label: "Software", value: effectiveNip11.software },
                  { label: "Version", value: effectiveNip11.version },
                  { label: "Contact", value: effectiveNip11.contact },
                  { label: "Pubkey", value: effectiveNip11.pubkey ? `${effectiveNip11.pubkey.slice(0, 16)}…` : undefined },
                  { label: "Location", value: relay.countryName },
                  { label: "Countries", value: effectiveNip11.relay_countries?.join(', ') },
                  { label: "Languages", value: effectiveNip11.language_tags?.join(', ') },
                  { label: "Last Checked", value: timeAgo(relay.lastChecked) },
                ].map(({ label, value }) =>
                  value ? (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium font-mono text-xs text-right max-w-[200px] truncate">{value}</span>
                    </div>
                  ) : null
                )}
              </CardContent>
            </Card>

            {/* Limitations */}
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  Limitations & Policies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {effectiveNip11.limitation && Object.entries(effectiveNip11.limitation).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground text-xs">{key.replace(/_/g, " ")}</span>
                    <span className="font-medium text-xs">
                      {typeof val === "boolean" ? (
                        val ? (
                          <span className="text-yellow-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Yes</span>
                        ) : (
                          <span className="text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> No</span>
                        )
                      ) : (
                        String(val)
                      )}
                    </span>
                  </div>
                ))}
                {(!effectiveNip11.limitation || Object.keys(effectiveNip11.limitation).length === 0) && (
                  <p className="text-sm text-muted-foreground">No limitations specified</p>
                )}
                {/* Posting Policy + Terms links */}
                {effectiveNip11.posting_policy && (
                  <div className="flex items-center justify-between text-sm pt-1 border-t border-border/40">
                    <span className="text-muted-foreground text-xs">Posting Policy</span>
                    <a href={effectiveNip11.posting_policy} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                      View <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                )}
                {effectiveNip11.payments_url && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground text-xs">Payments</span>
                    <a href={effectiveNip11.payments_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                      View <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Live Connection Test */}
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Wifi className="w-4 h-4 text-primary" />
                Live Connection Test
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => {
                    resetTest();
                    setTimeout(() => runTest(relay.url), 50);
                  }}
                  disabled={testResult.status === "connecting"}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  {testResult.status === "connecting" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Testing…</>
                  ) : (
                    <><Wifi className="w-4 h-4" /> Test Connection</>
                  )}
                </Button>

                {testStatusIcon && (
                  <div className="flex items-center gap-2 text-sm">
                    {testStatusIcon}
                    <span className={
                      testResult.status === "connected" ? "text-emerald-500" :
                      testResult.status === "failed" || testResult.status === "timeout" ? "text-red-500" :
                      "text-muted-foreground"
                    }>
                      {testResult.status === "connected" && `Connected in ${testResult.latencyMs}ms`}
                      {testResult.status === "failed" && (testResult.error ?? "Connection failed")}
                      {testResult.status === "timeout" && "Timed out (8s)"}
                    </span>
                  </div>
                )}

                {testResult.status === "idle" && (
                  <span className="text-xs text-muted-foreground">
                    Test a live WebSocket connection to this relay
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* What it's best for */}
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                What This Relay Is Best For
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relay.useCases.map((uc) => {
                  const descriptions: Partial<Record<typeof uc, string>> = {
                    General: "Ideal for everyday Nostr usage: posting notes, following users, and reading your feed.",
                    "High Performance": "Optimized for speed with low latency and high throughput for power users.",
                    "Paid Access": "Premium relay with payment-gated access ensuring higher quality content and less spam.",
                    Privacy: "Designed with privacy in mind, with strong data policies and minimal logging.",
                    "Censorship Resistant": "Committed to free speech with minimal moderation and content restrictions.",
                    Communities: "Great for Nostr communities, groups, and collaborative spaces.",
                    Inbox: "Perfect as a personal inbox relay for receiving DMs and mentions reliably.",
                    DMs: "Optimized for encrypted direct messages with reliable delivery.",
                    Archive: "Long-term event storage and retrieval for historical Nostr data.",
                    Zaps: "Supports Lightning zaps and payment integrations seamlessly.",
                  };
                  return (
                    <div key={uc} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-sm">{uc}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {descriptions[uc] ?? `This relay supports ${uc} use cases.`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Health Score + Operator Profile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <HealthScoreCard relay={relay as LiveRelayRecord} />
            {effectiveNip11.pubkey && (
              <OperatorProfile pubkey={effectiveNip11.pubkey} />
            )}
          </div>

          {/* NIP-11 document validation */}
          <Nip11ValidationCard nip11={effectiveNip11} />
        </TabsContent>

        {/* Verify Tab */}
        <TabsContent value="verify" className="space-y-5">
          <NIPVerificationPanel relay={relay as LiveRelayRecord} />
        </TabsContent>

        {/* Uptime Tab */}
        <TabsContent value="uptime" className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "30-Day Uptime", value: `${relay.uptimePercent30d.toFixed(2)}%`, sub: "Last 30 days" },
              { label: "Avg Latency", value: formatLatency(relay.avgLatencyMs), sub: "Round-trip time" },
              { label: "Trust Score", value: `${relay.trustScore}/100`, sub: "Composite score" },
            ].map((s) => (
              <Card key={s.label} className="border-border/60 text-center">
                <CardContent className="pt-5 pb-4">
                  <div className="text-3xl font-black mb-1">{s.value}</div>
                  <div className="font-semibold text-sm">{s.label}</div>
                  <div className="text-xs text-muted-foreground">{s.sub}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Real observed uptime from NIP-66 monitor history */}
          <RealUptimePanel relayUrl={relay.url} />

          {/* Peer percentile benchmarks */}
          <PeerBenchmarksCard relay={relay as LiveRelayRecord} />

          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Uptime History</CardTitle>
            </CardHeader>
            <CardContent>
              <UptimeHistoryChart relay={relay} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* NIP-66 Tab */}
        <TabsContent value="nip66" className="space-y-4">
          {/* Multi-monitor consensus — always shown, even without seed enrichment */}
          <MonitorConsensusCard relayUrl={relay.url} />

          {!relay.nip66?.enriched && !monitorEvent ? (
            <Card className="border-border/60">
              <CardContent className="py-10 text-center">
                <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-bold mb-2">No NIP-66 Data Available</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  This relay hasn't been picked up by a nostr.watch-style NIP-66 monitor yet.
                  NIP-66 data comes from kind:30166 (Relay Discovery) and kind:10166 (Monitor Announcements) events.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* NIP-66 Status */}
              <Card className="border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4 text-violet-500" />
                    NIP-66 Monitor Data
                    {monitorEvent && (
                      <span className="text-xs bg-emerald-500/15 text-emerald-500 border border-emerald-500/25 px-1.5 py-0.5 rounded-full font-medium ml-auto">
                        Live
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { label: "Live Status", value: relay.nip66?.liveStatus ?? (monitorEvent ? "online" : "unknown") },
                    { label: "RTT Open", value: monitorEvent?.rttOpen != null ? `${monitorEvent.rttOpen}ms` : (relay.nip66?.monitorLatencyMs != null ? `${relay.nip66.monitorLatencyMs}ms` : "N/A") },
                    { label: "RTT Read", value: monitorEvent?.rttRead != null ? `${monitorEvent.rttRead}ms` : "N/A" },
                    { label: "RTT Write", value: monitorEvent?.rttWrite != null ? `${monitorEvent.rttWrite}ms` : "N/A" },
                    { label: "Last Event", value: monitorEvent ? timeAgo(monitorEvent.checkedAt * 1000) : (relay.nip66?.lastMonitorEvent ? timeAgo(relay.nip66.lastMonitorEvent) : "N/A") },
                    { label: "Network", value: monitorEvent?.network ?? "clearnet" },
                    { label: "Relay Type", value: monitorEvent?.relayType ?? "Standard" },
                    { label: "Events/Day", value: relay.nip66?.eventsPerDay?.toLocaleString() ?? "N/A" },
                    { label: "Connected Users", value: relay.nip66?.connectedUsers?.toLocaleString() ?? "N/A" },
                    { label: "Monitor", value: (monitorEvent?.monitorPubkey ?? relay.nip66?.monitorPubkey) ? `${(monitorEvent?.monitorPubkey ?? relay.nip66?.monitorPubkey ?? '').slice(0, 16)}…` : "N/A" },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium font-mono text-xs">{value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Capabilities */}
              <Card className="border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-4 h-4 text-violet-500" />
                    Capabilities (NIP-66)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {relay.nip66.capabilities && Object.entries(relay.nip66.capabilities).map(([cap, val]) => (
                    <div key={cap} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground capitalize">{cap.replace(/([A-Z])/g, ' $1')}</span>
                      {val ? (
                        <span className="text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span>
                      ) : (
                        <span className="text-muted-foreground flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> No</span>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Geolocation from geohash */}
              {geoCoords && monitorEvent?.geohash && (
                <Card className="border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-violet-500" />
                      Geolocation (NIP-66)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: "Geohash", value: monitorEvent.geohash },
                      { label: "Latitude", value: geoCoords.lat.toFixed(4) },
                      { label: "Longitude", value: geoCoords.lng.toFixed(4) },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-medium font-mono text-xs">{value}</span>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground mt-2">
                      Location derived from NIP-66 monitor geohash. Precision depends on geohash length.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Monitor requirements */}
              {monitorEvent && (
                <Card className="border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="w-4 h-4 text-violet-500" />
                      Requirements (NIP-66)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {[
                      { label: "Auth Required", val: monitorEvent.requirements.auth },
                      { label: "Payment Required", val: monitorEvent.requirements.payment },
                      { label: "PoW Required", val: monitorEvent.requirements.pow },
                      { label: "Accepts Writes", val: monitorEvent.requirements.writes },
                    ].map(({ label, val }) => (
                      <div key={label} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{label}</span>
                        {val ? (
                          <span className="text-yellow-500 flex items-center gap-1 text-xs"><AlertCircle className="w-3 h-3" /> Yes</span>
                        ) : (
                          <span className="text-emerald-500 flex items-center gap-1 text-xs"><CheckCircle2 className="w-3 h-3" /> No</span>
                        )}
                      </div>
                    ))}
                    {monitorEvent.supportedNips.length > 0 && (
                      <div className="pt-2 border-t border-border/40">
                        <p className="text-xs text-muted-foreground mb-1.5 font-medium">NIPs from Monitor ({monitorEvent.supportedNips.length})</p>
                        <div className="flex flex-wrap gap-1">
                          {monitorEvent.supportedNips.map((nip) => (
                            <span key={nip} className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-mono">
                              {String(nip).padStart(2, '0')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Conflict warning */}
              {relay.nip66?.conflictsWithNip11 && (
                <Card className="border-yellow-500/30 bg-yellow-500/5 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-yellow-500">
                      <AlertCircle className="w-4 h-4" />
                      NIP-66 / NIP-11 Conflict Detected
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{relay.nip66.conflictDetail}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      This discrepancy has been flagged for moderation review. Data may be temporarily inconsistent.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Import sources */}
              {relay.importSources && relay.importSources.length > 0 && (
                <Card className="border-border/60 md:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Download className="w-4 h-4 text-primary" />
                      Data Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {relay.importSources.map((src, i) => (
                        <div key={i} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2 text-xs">
                          <span className="font-semibold">{src.source}</span>
                          <span className="text-muted-foreground">{timeAgo(src.importedAt)}</span>
                          {src.fieldsUpdated && (
                            <span className="text-muted-foreground opacity-70">→ {src.fieldsUpdated.join(', ')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Community Tab */}
        <TabsContent value="community" className="space-y-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ThumbsUp className="w-4 h-4 text-primary" />
                Community Votes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VotingPanel relay={relay} />
            </CardContent>
          </Card>

          {relay.relayToolsUrl && (
            <Card className="border-border/60 bg-gradient-to-br from-primary/5 to-violet-500/5">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm mb-0.5">One-click add via relay.tools</h3>
                    <p className="text-xs text-muted-foreground">
                      Use relay.tools to add this relay to your Nostr client in one click.
                    </p>
                  </div>
                  <a href={relay.relayToolsUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="gap-2 flex-shrink-0">
                      Open relay.tools <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Comments Tab (NIP-22) */}
        <TabsContent value="comments" className="space-y-4">
          <RelayCommentSection relayUrl={relay.url} />
        </TabsContent>

        {/* Labels Tab (NIP-32 + NIP-85) */}
        <TabsContent value="labels" className="space-y-4">
          <RelayLabelPanel relayUrl={relay.url} />
        </TabsContent>

        {/* Membership Tab (NIP-43) */}
        <TabsContent value="membership" className="space-y-4">
          <RelayMembershipPanel relayUrl={relay.url} relaySelfPubkey={relaySelfPubkey} />
          <RelayManagementPanel relayUrl={relay.url} />
        </TabsContent>

        {/* Auto-Tags Tab */}
        <TabsContent value="autotags" className="space-y-4">
          <AutoTagsPanel relay={relay} />
        </TabsContent>

        {/* NIPs Tab */}
        <TabsContent value="nips" className="space-y-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Code2 className="w-4 h-4 text-primary" />
                Supported NIPs ({nips.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {nips.length === 0 ? (
                <p className="text-sm text-muted-foreground">NIP support information not available.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {nips.map((nip) => (
                    <a
                      key={nip}
                      href={`https://github.com/nostr-protocol/nips/blob/master/${String(nip).padStart(2, "0")}.md`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 p-2.5 rounded-lg border border-border/50 hover:border-primary/30 hover:bg-primary/5 transition-all"
                    >
                      <span className="font-mono text-xs font-bold text-primary">
                        {String(nip).padStart(2, "0")}
                      </span>
                      <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors truncate">
                        {getNipName(nip)}
                      </span>
                      <ExternalLink className="w-2.5 h-2.5 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 flex-shrink-0" />
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* NIP-89 App Handler Recommendations */}
          {nips.length > 0 && (
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  Compatible Apps
                  <Badge variant="outline" className="text-xs ml-auto border-primary/30 text-primary">
                    NIP-89
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Apps that can handle event kinds supported by this relay:
                </p>
                <div className="flex flex-wrap gap-2">
                  {nips.slice(0, 5).map((nip) => (
                    <a
                      key={nip}
                      href={`https://nostrhub.io/naddr1qvzqqqrcvypzqprpljlvcnpnw3pejvkkhrc3y6wvmd7vjuad0fg2ud3dky66gaxaqqxku6tswvkk7m3ddehhxarjqk4nmy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs bg-muted/50 hover:bg-muted border border-border/50 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <span className="font-mono font-bold text-primary">{String(nip).padStart(2, '0')}</span>
                      <span className="text-muted-foreground">Find Apps</span>
                      <ExternalLink className="w-2.5 h-2.5 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* NIP-B7 Blossom Health */}
          {relay.blossomSupported && (
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-sky-500" />
                  Blossom Servers
                  <Badge variant="outline" className="text-xs ml-auto border-sky-500/30 text-sky-500">
                    NIP-B7
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Blossom media servers associated with this relay. Files are addressable by SHA-256 hash.
                </p>
                <div className="space-y-2">
                  {[
                    { url: relay.url.replace('wss://', 'https://').replace('ws://', 'http://'), status: 'online' },
                  ].map((server) => (
                    <div key={server.url} className="flex items-center gap-2 p-2 rounded-lg border border-border/30 bg-card/50">
                      <div className={`w-2 h-2 rounded-full ${server.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <code className="text-xs font-mono text-muted-foreground truncate flex-1">{server.url}</code>
                      <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-500">
                        Online
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing" className="space-y-4">
          {relay.priceTiers.length === 0 ? (
            <Card className="border-border/60">
              <CardContent className="py-8 text-center text-muted-foreground">
                Pricing information not available for this relay.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {relay.priceTiers.map((tier, i) => (
                <Card
                  key={i}
                  className={`border-border/60 ${!relay.isFree && i === 1 ? "border-primary/30 bg-primary/5" : ""}`}
                >
                  {!relay.isFree && i === 1 && (
                    <div className="bg-primary text-primary-foreground text-xs font-bold text-center py-1 rounded-t-xl">
                      RECOMMENDED
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{tier.name}</CardTitle>
                      <div className="text-right">
                        <div className="text-2xl font-black">
                          {tier.price === 0 ? (
                            <span className="text-emerald-500">Free</span>
                          ) : (
                            <span>{formatPrice(tier)}</span>
                          )}
                        </div>
                        {tier.billing && <div className="text-xs text-muted-foreground">per {tier.billing === "monthly" ? "month" : tier.billing}</div>}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1.5">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {tier.price > 0 && relay.paymentUrl && (
                      <a href={relay.paymentUrl} target="_blank" rel="noopener noreferrer" className="block mt-4">
                        <Button className="w-full gap-2" size="sm" variant={i === 1 ? "default" : "outline"}>
                          <DollarSign className="w-3.5 h-3.5" />
                          Subscribe
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Add to Client Tab */}
        <TabsContent value="add-to-client" className="space-y-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Add to Your Nostr Client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Relay URL with copy */}
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Relay URL</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted/60 border border-border/40 rounded-lg px-3 py-2 text-sm font-mono text-foreground">
                    {relay.url}
                  </code>
                  <Button variant="outline" size="sm" className="gap-1.5 flex-shrink-0" onClick={handleCopy}>
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Client selector */}
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Client Instructions</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(CLIENT_INSTRUCTIONS).map(([key, client]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedClient(key)}
                      className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition-all ${
                        selectedClient === key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      {client.name}
                    </button>
                  ))}
                </div>

                {selectedClient && CLIENT_INSTRUCTIONS[selectedClient] && (
                  <ol className="space-y-2">
                    {CLIENT_INSTRUCTIONS[selectedClient].steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <span className="text-foreground">{step}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Report button */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-2">Found incorrect information?</p>
            <Link to="/submit">
              <Button variant="outline" size="sm" className="gap-2">
                <AlertCircle className="w-3.5 h-3.5" />
                Report an Issue / Submit Correction
              </Button>
            </Link>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
